import React, { useState, useEffect, useRef } from 'react';
import { Mail, Send, Check, X, User, MessageSquare, Clock, Filter, Users, Search, ChevronDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const AdminMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  
  // New state for improved messaging
  const [allUsers, setAllUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [directMessageText, setDirectMessageText] = useState('');
  const [sendToAll, setSendToAll] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  
  const dropdownRef = useRef(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchMessages();
    fetchAllUsers();
  }, [statusFilter, page]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter users based on search query
  useEffect(() => {
    if (userSearchQuery.trim() === '') {
      setFilteredUsers(allUsers);
    } else {
      const query = userSearchQuery.toLowerCase();
      const filtered = allUsers.filter(user => 
        user.firstName?.toLowerCase().includes(query) ||
        user.lastName?.toLowerCase().includes(query) ||
        user.email?.toLowerCase().includes(query)
      );
      setFilteredUsers(filtered);
    }
  }, [userSearchQuery, allUsers]);

  const fetchAllUsers = async () => {
    try {
      setLoadingUsers(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/contact/admin/all-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllUsers(response.data.users || []);
      setFilteredUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const url = statusFilter === 'all' 
        ? `${API_URL}/api/contact/admin/messages?page=${page}`
        : `${API_URL}/api/contact/admin/messages?status=${statusFilter}&page=${page}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMessages(response.data.messages || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast({
        title: "Error",
        description: "Failed to load messages",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReply = async (messageId) => {
    if (!replyText.trim()) {
      toast({
        title: "Error",
        description: "Please enter a reply message",
        variant: "destructive"
      });
      return;
    }

    try {
      setSending(true);
      const token = localStorage.getItem('token');
      
      await axios.post(
        `${API_URL}/api/contact/admin/reply/${messageId}`,
        { message: replyText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast({
        title: "Success",
        description: "Reply sent successfully"
      });
      
      setReplyText('');
      setSelectedMessage(null);
      fetchMessages();
    } catch (error) {
      console.error('Error sending reply:', error);
      toast({
        title: "Error",
        description: "Failed to send reply",
        variant: "destructive"
      });
    } finally {
      setSending(false);
    }
  };

  const updateStatus = async (messageId, status) => {
    try {
      const token = localStorage.getItem('token');
      
      await axios.patch(
        `${API_URL}/api/contact/admin/status/${messageId}?status=${status}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast({
        title: "Success",
        description: `Status updated to ${status}`
      });
      
      fetchMessages();
    } catch (error) {
      console.error('Error updating status:', error);
      toast({
        title: "Error",
        description: "Failed to update status",
        variant: "destructive"
      });
    }
  };

  const deleteMessage = async (messageId) => {
    if (!confirm('Are you sure you want to delete this message?')) return;

    try {
      const token = localStorage.getItem('token');
      
      await axios.delete(
        `${API_URL}/api/contact/admin/delete/${messageId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast({
        title: "Success",
        description: "Message deleted"
      });
      
      setSelectedMessage(null);
      fetchMessages();
    } catch (error) {
      console.error('Error deleting message:', error);
      toast({
        title: "Error",
        description: "Failed to delete message",
        variant: "destructive"
      });
    }
  };

  const handleUserSelect = (user) => {
    const isSelected = selectedUsers.some(u => u.id === user.id);
    if (isSelected) {
      setSelectedUsers(selectedUsers.filter(u => u.id !== user.id));
    } else {
      setSelectedUsers([...selectedUsers, user]);
    }
  };

  const removeSelectedUser = (userId) => {
    setSelectedUsers(selectedUsers.filter(u => u.id !== userId));
  };

  const sendDirectMessage = async () => {
    if (!directMessageText.trim()) {
      toast({
        title: "Error",
        description: "Please enter a message",
        variant: "destructive"
      });
      return;
    }

    if (!sendToAll && selectedUsers.length === 0) {
      toast({
        title: "Error",
        description: "Please select at least one user or choose 'Send to All'",
        variant: "destructive"
      });
      return;
    }

    try {
      setSending(true);
      const token = localStorage.getItem('token');
      
      if (sendToAll) {
        // Send to all users
        await axios.post(
          `${API_URL}/api/contact/admin/message-all-users`,
          { message: directMessageText },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        toast({
          title: "Success",
          description: "Message sent to all users"
        });
      } else if (selectedUsers.length === 1) {
        // Send to single user
        await axios.post(
          `${API_URL}/api/contact/admin/message-user/${selectedUsers[0].id}`,
          { message: directMessageText },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        toast({
          title: "Success",
          description: `Message sent to ${selectedUsers[0].firstName} ${selectedUsers[0].lastName}`
        });
      } else {
        // Send to multiple users
        await axios.post(
          `${API_URL}/api/contact/admin/message-multiple-users`,
          { 
            user_ids: selectedUsers.map(u => u.id),
            message: directMessageText 
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        toast({
          title: "Success",
          description: `Message sent to ${selectedUsers.length} users`
        });
      }
      
      setDirectMessageText('');
      setSelectedUsers([]);
      setSendToAll(false);
    } catch (error) {
      console.error('Error sending direct message:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to send message",
        variant: "destructive"
      });
    } finally {
      setSending(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'replied': return 'bg-blue-100 text-blue-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  if (loading && messages.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mint-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Contact Messages</h1>
        <p className="text-gray-600">Manage contact form submissions and user messages</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Messages</p>
                <p className="text-2xl font-bold">{total}</p>
              </div>
              <Mail className="w-8 h-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Users</p>
                <p className="text-2xl font-bold">{allUsers.length}</p>
              </div>
              <Users className="w-8 h-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Direct Message Section - Improved */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            Send Direct Message to Users
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Send to All Toggle */}
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={sendToAll}
                  onChange={(e) => {
                    setSendToAll(e.target.checked);
                    if (e.target.checked) {
                      setSelectedUsers([]);
                    }
                  }}
                  className="w-4 h-4 text-mint-500 rounded focus:ring-mint-500"
                />
                <span className="font-medium text-gray-700">Send to All Users</span>
              </label>
              {sendToAll && (
                <Badge className="bg-green-100 text-green-800">
                  {allUsers.length} users will receive this message
                </Badge>
              )}
            </div>

            {/* User Selection Dropdown */}
            {!sendToAll && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Select Recipients
                </label>
                
                {/* Selected Users Display */}
                {selectedUsers.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {selectedUsers.map(user => (
                      <Badge 
                        key={user.id} 
                        className="bg-mint-100 text-mint-800 flex items-center gap-1 pr-1"
                      >
                        {user.firstName} {user.lastName}
                        <button
                          onClick={() => removeSelectedUser(user.id)}
                          className="ml-1 hover:bg-mint-200 rounded-full p-0.5"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
                
                {/* Searchable Dropdown */}
                <div className="relative" ref={dropdownRef}>
                  <div 
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-mint-500 focus-within:border-transparent cursor-pointer flex items-center justify-between"
                    onClick={() => setShowDropdown(!showDropdown)}
                  >
                    <div className="flex items-center gap-2 flex-1">
                      <Search className="w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={userSearchQuery}
                        onChange={(e) => {
                          setUserSearchQuery(e.target.value);
                          setShowDropdown(true);
                        }}
                        onFocus={() => setShowDropdown(true)}
                        placeholder={selectedUsers.length > 0 ? "Add more users..." : "Search users by name or email..."}
                        className="flex-1 outline-none bg-transparent"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
                  </div>
                  
                  {/* Dropdown List */}
                  {showDropdown && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                      {loadingUsers ? (
                        <div className="p-4 text-center text-gray-500">
                          Loading users...
                        </div>
                      ) : filteredUsers.length === 0 ? (
                        <div className="p-4 text-center text-gray-500">
                          No users found
                        </div>
                      ) : (
                        filteredUsers.map(user => {
                          const isSelected = selectedUsers.some(u => u.id === user.id);
                          return (
                            <div
                              key={user.id}
                              onClick={() => handleUserSelect(user)}
                              className={`px-4 py-3 cursor-pointer flex items-center gap-3 hover:bg-gray-50 ${
                                isSelected ? 'bg-mint-50' : ''
                              }`}
                            >
                              {/* User Avatar */}
                              <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                                {user.image ? (
                                  <img src={user.image} alt="" className="w-full h-full object-cover" />
                                ) : (
                                  <User className="w-4 h-4 text-gray-500" />
                                )}
                              </div>
                              
                              {/* User Info */}
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-gray-900 truncate">
                                  {user.firstName} {user.lastName}
                                </p>
                                <p className="text-sm text-gray-500 truncate">{user.email}</p>
                              </div>
                              
                              {/* Selection Indicator */}
                              {isSelected && (
                                <Check className="w-5 h-5 text-mint-500 flex-shrink-0" />
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
                
                <p className="text-xs text-gray-500">
                  {selectedUsers.length} user(s) selected
                </p>
              </div>
            )}

            {/* Message Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Message
              </label>
              <textarea
                value={directMessageText}
                onChange={(e) => setDirectMessageText(e.target.value)}
                placeholder="Type your message to send..."
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-transparent"
              />
            </div>

            {/* Send Button */}
            <Button
              onClick={sendDirectMessage}
              disabled={sending || (!sendToAll && selectedUsers.length === 0) || !directMessageText.trim()}
              className="bg-mint-500 hover:bg-mint-600"
            >
              <Send className="w-4 h-4 mr-2" />
              {sending ? 'Sending...' : sendToAll ? `Send to All (${allUsers.length})` : `Send to ${selectedUsers.length} User(s)`}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Filter */}
      <div className="flex items-center gap-4">
        <Filter className="w-5 h-5 text-gray-600" />
        <div className="flex gap-2">
          {['all', 'pending', 'replied', 'resolved'].map((filter) => (
            <button
              key={filter}
              onClick={() => {
                setStatusFilter(filter);
                setPage(1);
              }}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                statusFilter === filter
                  ? 'bg-mint-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {filter.charAt(0).toUpperCase() + filter.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Messages List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Messages Inbox */}
        <Card>
          <CardHeader>
            <CardTitle>Messages Inbox</CardTitle>
          </CardHeader>
          <CardContent>
            {messages.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Mail className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p>No messages found</p>
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    onClick={() => setSelectedMessage(msg)}
                    className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                      selectedMessage?.id === msg.id
                        ? 'border-mint-500 bg-cream-100'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900">{msg.name}</h4>
                        <p className="text-sm text-gray-600">{msg.email}</p>
                      </div>
                      <Badge className={getStatusColor(msg.status)}>
                        {msg.status}
                      </Badge>
                    </div>
                    <p className="font-medium text-gray-800 mb-2">{msg.subject}</p>
                    <p className="text-sm text-gray-600 line-clamp-2">{msg.message}</p>
                    <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                      <Clock className="w-3 h-3" />
                      {formatDate(msg.createdAt)}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Pagination */}
            {total > 20 && (
              <div className="flex justify-center gap-2 mt-4">
                <Button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  variant="outline"
                  size="sm"
                >
                  Previous
                </Button>
                <span className="px-4 py-2 text-sm">Page {page}</span>
                <Button
                  onClick={() => setPage(page + 1)}
                  disabled={page * 20 >= total}
                  variant="outline"
                  size="sm"
                >
                  Next
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Message Detail & Reply */}
        <Card>
          <CardHeader>
            <CardTitle>Message Details</CardTitle>
          </CardHeader>
          <CardContent>
            {!selectedMessage ? (
              <div className="text-center py-12 text-gray-500">
                <MessageSquare className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p>Select a message to view details</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Message Info */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-sm text-gray-600">From</p>
                      <p className="font-semibold">{selectedMessage.name}</p>
                      <p className="text-sm text-gray-600">{selectedMessage.email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Status</p>
                      <Badge className={getStatusColor(selectedMessage.status)}>
                        {selectedMessage.status}
                      </Badge>
                    </div>
                  </div>
                  <div className="mb-4">
                    <p className="text-sm text-gray-600">Subject</p>
                    <p className="font-semibold">{selectedMessage.subject}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-2">Message</p>
                    <p className="text-gray-800">{selectedMessage.message}</p>
                  </div>
                  <p className="text-xs text-gray-500 mt-4">
                    Received: {formatDate(selectedMessage.createdAt)}
                  </p>
                </div>

                {/* Previous Replies */}
                {selectedMessage.replies && selectedMessage.replies.length > 0 && (
                  <div className="space-y-2">
                    <p className="font-semibold text-gray-700">Previous Replies:</p>
                    {selectedMessage.replies.map((reply, index) => (
                      <div key={index} className="bg-blue-50 p-3 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-sm font-medium text-blue-900">
                            {reply.adminName}
                          </p>
                          <p className="text-xs text-blue-600">
                            {formatDate(reply.timestamp)}
                          </p>
                        </div>
                        <p className="text-sm text-gray-700">{reply.message}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Reply Form */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Your Reply
                  </label>
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Type your reply..."
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-transparent"
                  />
                  <div className="flex gap-2 mt-2">
                    <Button
                      onClick={() => handleReply(selectedMessage.id)}
                      disabled={sending}
                      className="flex-1 bg-mint-500 hover:bg-mint-600"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      {sending ? 'Sending...' : 'Send Reply'}
                    </Button>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-2 pt-4 border-t">
                  <Button
                    onClick={() => updateStatus(selectedMessage.id, 'resolved')}
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-1"
                  >
                    <Check className="w-4 h-4" />
                    Mark Resolved
                  </Button>
                  <Button
                    onClick={() => updateStatus(selectedMessage.id, 'pending')}
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-1"
                  >
                    <Clock className="w-4 h-4" />
                    Mark Pending
                  </Button>
                  <Button
                    onClick={() => deleteMessage(selectedMessage.id)}
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-1 text-navy-500 hover:bg-cream-100"
                  >
                    <X className="w-4 h-4" />
                    Delete
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminMessages;
